import { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { CrossOutlined } from "components/icons/CrossOutlined";
import {
  PLATFORM_TOUR_COMPLETED,
  PLATFORM_TOUR_PLAYER_CLOSED,
} from "constants/analyticsEvents";
import {
  PLATFORM_TOUR_LAYOUT_DURATION_S,
  PLATFORM_TOUR_LAYOUT_ID,
  PLATFORM_TOUR_LAYOUT_TRANSITION,
  PLATFORM_TOUR_SOURCE,
  PLATFORM_TOUR_VIDEO_URL,
} from "constants/productTour";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppSelector } from "hooks/useAppSelector";
import { useToast } from "hooks/useToast";

type PlatformTourPlayerModalProps = {
  open: boolean;
  onClose: (isCompleted: boolean, video?: HTMLVideoElement | null) => void;
};

export const PlatformTourPlayerModal = ({
  open,
  onClose,
}: PlatformTourPlayerModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trackEvent } = useAnalytics();
  const { success } = useToast();
  const playerSource = useAppSelector((state) => state.platformTour.source);

  const handleClose = () => {
    trackEvent(PLATFORM_TOUR_PLAYER_CLOSED, {
      watched_seconds: Math.round(videoRef.current?.currentTime ?? 0),
    });
    videoRef.current?.pause();
    onClose(false, videoRef.current);
  };

  const handleEnded = () => {
    trackEvent(PLATFORM_TOUR_COMPLETED);
    onClose(true);

    if (playerSource === PLATFORM_TOUR_SOURCE.GET_HELP) return;

    success({
      title: "Tour completed",
      message: "Revisit it anytime from Get help",
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) handleClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <Modal.Root open onOpenChange={handleOpenChange}>
          <Modal.Portal forceMount>
            <Modal.Overlay asChild forceMount>
              <motion.div
                exit={{
                  opacity: 0,
                  transition: {
                    duration: PLATFORM_TOUR_LAYOUT_DURATION_S * 0.5,
                  },
                }}
              />
            </Modal.Overlay>
            <Modal.RawContent asChild forceMount aria-describedby={undefined}>
              <motion.div
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                style={{ borderRadius: 12 }}
                className="t-fixed t-inset-0 t-z-modal t-m-auto t-h-fit t-w-[960px] t-max-w-[90vw]"
              >
                <Modal.Title className="t-sr-only">Platform tour</Modal.Title>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: {
                      delay: PLATFORM_TOUR_LAYOUT_DURATION_S,
                      duration: 0.2,
                    },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.2 } }}
                  className="t-absolute t-right-3 t-top-3 t-z-10"
                >
                  <Modal.RawClose asChild>
                    <Button
                      customType="ghost_icon"
                      size="small"
                      className="hover:!t-bg-surface-lighter-grey"
                    >
                      <CrossOutlined size="18" />
                    </Button>
                  </Modal.RawClose>
                </motion.div>
                <motion.video
                  ref={videoRef}
                  layoutId={PLATFORM_TOUR_LAYOUT_ID.MEDIA}
                  layoutCrossfade={false}
                  transition={PLATFORM_TOUR_LAYOUT_TRANSITION}
                  style={{ borderRadius: 12 }}
                  src={PLATFORM_TOUR_VIDEO_URL}
                  className="t-relative t-block t-aspect-video t-w-full t-bg-text-100"
                  controls
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  autoPlay
                  playsInline
                  preload="metadata"
                  onEnded={handleEnded}
                />
              </motion.div>
            </Modal.RawContent>
          </Modal.Portal>
        </Modal.Root>
      )}
    </AnimatePresence>
  );
};
